import SidebarSection from "./SidebarSection.jsx";
import CollapsedOverlay from "./CollapsedOverlay.jsx";
import NestedOverlay from "./NestedOverlay.jsx";
import { useSidebarMenuItems } from "../../../hooks/useSidebarMenuItems.js";
import { ChevronLeft, ChevronRight, LayoutDashboard } from "lucide-react";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  fontStack,
  typographyTokens,
  mapFontWeight,
  colorTokens,
  pxValue,
} from "../../../styles/designTokens";
import logoIcon from "../../../assets/icons/k.png";

function mergeClasses(...classes) {
  return classes.filter(Boolean).join(" ");
}

const COLLAPSED_WIDTH = 88;
const EXPANDED_WIDTH = 288;
export const SIDEBAR_COLLAPSED_WIDTH = COLLAPSED_WIDTH;
export const SIDEBAR_EXPANDED_WIDTH = EXPANDED_WIDTH;

function LogoMark() {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-black ">
      <img
        src={logoIcon}
        alt="Kala logo"
        className="h-10 w-10 object-contain"
        draggable="false"
      />
    </div>
  );
}

export default function Sidebar({ onCollapseStateChange }) {
  const filteredMenuItems = useSidebarMenuItems();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const sidebarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [pendingSection, setPendingSection] = useState(null);
  const [autoCollapseOnSelect, setAutoCollapseOnSelect] = useState(false);
  const [suppressCollapsedHover, setSuppressCollapsedHover] = useState(false);
  const hoverSuppressionTimeoutRef = useRef(null);
  const [scrollThumb, setScrollThumb] = useState({ top: 0, height: 0, scrollable: false });
  const [hasScrolled, setHasScrolled] = useState(false);
  const [collapsedOverlay, setCollapsedOverlay] = useState(null);
  const [nestedOverlay, setNestedOverlay] = useState(null);
  const overlayHideTimeoutRef = useRef(null);
  const nestedHideTimeoutRef = useRef(null);

  const actionSecondaryActive = colorTokens?.actionSecondaryActive || "#e4e4e4";
  const actionSecondaryBase2 = colorTokens?.actionSecondaryBase2 || "#f2f2f2";
  const sectionLabelClass = "text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2";
  const sidebarActiveAccent =
    colorTokens?.interactionPurpleBase || colorTokens?.actionPrimaryBase || "#6366f1";
  const location = useLocation();
  const activePath = location.pathname;
  const dashboardActive = activePath.startsWith("/home");

  const navTypography = useMemo(() => {
    const smallToken = typographyTokens.bodySmallMedium || {};
    const fontFamily = smallToken.fontFamily || fontStack;
    const fontSize = pxValue(smallToken.fontSize || 12);
    const fontWeight = mapFontWeight(smallToken.fontWeight || "Medium");
    return { fontFamily, fontSize, fontWeight, activeFontWeight: mapFontWeight("SemiBold") };
  }, []);

  const buildNavItemStyle = (isActive) => {
    if (!navTypography) {
      return isActive
        ? { backgroundColor: actionSecondaryActive, "--sidebar-active-accent": sidebarActiveAccent }
        : undefined;
    }
    return {
      fontFamily: navTypography.fontFamily,
      fontSize: navTypography.fontSize,
      fontWeight: isActive
        ? navTypography.activeFontWeight || navTypography.fontWeight
        : navTypography.fontWeight,
      backgroundColor: isActive ? actionSecondaryActive : undefined,
      ...(isActive ? { "--sidebar-active-accent": sidebarActiveAccent } : {}),
    };
  };

  const handleHoverEnter = (event, isActive) => {
    if (isActive) return;
    event.currentTarget.style.backgroundColor = actionSecondaryBase2;
  };
  const handleHoverLeave = (event, isActive) => {
    event.currentTarget.style.backgroundColor = isActive ? actionSecondaryActive : "";
  };

  useEffect(() => {
    if (!isCollapsed && collapsedOverlay) {
      setCollapsedOverlay(null);
      setNestedOverlay(null);
    }
  }, [isCollapsed, collapsedOverlay]);

  useEffect(() => {
    const width = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    document.documentElement.style.setProperty("--sidebar-width", `${width}px`);
    onCollapseStateChange?.({ isCollapsed, width });
  }, [isCollapsed, onCollapseStateChange]);

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty("--sidebar-width", "0px");
      onCollapseStateChange?.({ isCollapsed: true, width: 0 });
      if (hoverSuppressionTimeoutRef.current) clearTimeout(hoverSuppressionTimeoutRef.current);
    };
  }, [onCollapseStateChange]);

  const triggerHoverSuppression = () => {
    if (hoverSuppressionTimeoutRef.current) clearTimeout(hoverSuppressionTimeoutRef.current);
    setSuppressCollapsedHover(true);
    hoverSuppressionTimeoutRef.current = setTimeout(() => {
      setSuppressCollapsedHover(false);
      hoverSuppressionTimeoutRef.current = null;
    }, 350);
  };

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      if (!prev) setPendingSection(null);
      setAutoCollapseOnSelect(false);
      setSuppressCollapsedHover(false);
      return !prev;
    });
  };

  const collapseSidebar = useCallback((options = {}) => {
    const { suppressHover = false } = options;
    setIsCollapsed(true);
    setPendingSection(null);
    setAutoCollapseOnSelect(false);
    if (suppressHover) triggerHoverSuppression();
  }, []);

  const hideCollapsedOverlay = useCallback((options = {}) => {
    const { immediate = false } = options;
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = null;
    }
    if (immediate) {
      setCollapsedOverlay(null);
      setNestedOverlay(null);
      return;
    }
    overlayHideTimeoutRef.current = setTimeout(() => {
      setCollapsedOverlay(null);
      setNestedOverlay(null);
    }, 120);
  }, []);

  const showCollapsedOverlay = (payload) => {
    if (suppressCollapsedHover) return;
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = null;
    }
    setCollapsedOverlay({ ...payload, open: true });
    setNestedOverlay(null);
  };

  const hideNestedOverlay = useCallback(() => {
    if (nestedHideTimeoutRef.current) clearTimeout(nestedHideTimeoutRef.current);
    nestedHideTimeoutRef.current = setTimeout(() => setNestedOverlay(null), 120);
  }, []);

  const showNestedOverlay = (payload) => {
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = null;
    }
    if (nestedHideTimeoutRef.current) {
      clearTimeout(nestedHideTimeoutRef.current);
      nestedHideTimeoutRef.current = null;
    }
    setNestedOverlay({ ...payload, open: true });
  };

  const handleCollapsedSectionSelect = (sectionName) => {
    if (!sectionName) return;
    hideCollapsedOverlay({ immediate: true });
    hideNestedOverlay();
    if (isCollapsed) {
      setIsCollapsed(false);
      setPendingSection(sectionName);
      setAutoCollapseOnSelect(true);
    } else {
      setPendingSection(sectionName);
    }
  };

  const handleItemSelect = () => {
    if (autoCollapseOnSelect || !isCollapsed) collapseSidebar({ suppressHover: true });
    setAutoCollapseOnSelect(false);
  };

  const handleOverlayItemClick = useCallback(() => {
    collapseSidebar();
    hideCollapsedOverlay({ immediate: true });
  }, [collapseSidebar, hideCollapsedOverlay]);

  const handleOverlayItemEnter = useCallback((e, children) => {
    if (children.length === 0) {
      hideNestedOverlay();
      return;
    }
    if (nestedHideTimeoutRef.current) {
      clearTimeout(nestedHideTimeoutRef.current);
      nestedHideTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    showNestedOverlay({ items: children, anchor: { top: rect.top, left: rect.right - 8 } });
  }, [hideNestedOverlay]);

  const handleOverlayItemLeave = useCallback((children) => {
    if (children.length) hideNestedOverlay();
  }, [hideNestedOverlay]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const updateThumb = () => {
      const { scrollHeight, clientHeight, scrollTop } = el;
      const scrollableSpace = scrollHeight - clientHeight;
      if (scrollableSpace <= 2) {
        setScrollThumb({ top: 0, height: 0, scrollable: false });
        setHasScrolled(false);
        return;
      }
      const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 36);
      const maxTop = clientHeight - thumbHeight;
      const top = ((scrollTop || 0) / (scrollHeight - clientHeight)) * (maxTop || 1);
      setScrollThumb({ top, height: thumbHeight, scrollable: true });
      const scrolled = (scrollTop || 0) > 1;
      setHasScrolled((prev) => (prev === scrolled ? prev : scrolled));
    };

    updateThumb();
    el.addEventListener("scroll", updateThumb);
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener("scroll", updateThumb);
      resizeObserver.disconnect();
    };
  }, [filteredMenuItems, isCollapsed]);

  return (
    <aside
      ref={sidebarRef}
      className={mergeClasses(
        "pointer-events-none",
        "fixed left-0 top-0 z-30 flex h-screen transition-all duration-500 ease-out",
        isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
      )}
      style={{
        fontFamily: navTypography.fontFamily,
        fontSize: navTypography.fontSize,
        fontWeight: navTypography.fontWeight,
        width: `${isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`,
        transition: "width 0.5s ease-out",
      }}
    >
      <div
        className="pointer-events-auto flex flex-col h-full border-r overflow-visible"
        style={{
          width: "100%",
          borderColor: colorTokens.borderPrimary || "#e2e8f0",
          background: colorTokens.backgroundPrimary || "#ffffff",
        }}
      >
        <header
          className={mergeClasses(
            "relative flex items-center h-[71px] border-b",
            isCollapsed ? "px-5 pl-[6px]" : "px-5"
          )}
          style={{ borderColor: colorTokens.borderPrimary || "#e2e8f0" }}
        >
          <div className={mergeClasses("flex items-center gap-3", isCollapsed && "justify-center")}>
            <div style={isCollapsed ? { padding: "6px" } : undefined}>
              <LogoMark />
            </div>
            {!isCollapsed && (
              <p
                className="font-medium"
                style={{
                  fontFamily: `"Inter", ${fontStack}`,
                  fontSize: "20px",
                  letterSpacing: "0",
                  lineHeight: "140%",
                  color: colorTokens.contentDarkPrimary || "#0f172a",
                }}
              >
                Kala
              </p>
            )}
          </div>
          <button
            onClick={toggleCollapse}
            className={mergeClasses(
              "absolute top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-md border pointer-events-auto",
              "shadow-[0_6px_16px_rgba(15,23,42,0.08)] transition-all duration-300",
              "right-[-14px]"
            )}
            style={{
              borderColor: colorTokens.borderPrimary || "#e2e8f0",
              background: colorTokens.backgroundTertiary || "#f8fafc",
              color: colorTokens.contentDarkTertiary || "#94a3b8",
            }}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <div className="flex items-center gap-[2px]">
                <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
                <ChevronLeft className="h-4 w-4" strokeWidth={2.2} />
              </div>
            ) : (
              <div className="flex items-center gap-[2px]">
                <ChevronLeft className="h-4 w-4" strokeWidth={2.2} />
                <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
              </div>
            )}
          </button>
        </header>

        <nav
          ref={scrollContainerRef}
          className={mergeClasses(
            "sidebar-scroll relative flex-1",
            isCollapsed ? "overflow-y-auto overflow-x-visible" : "overflow-y-auto",
            scrollThumb.scrollable && hasScrolled && "sidebar-scroll-active"
          )}
        >
          <div className={isCollapsed ? "px-2 pt-5" : "px-2 pt-5"}>
            {!isCollapsed && (
              <p className={sectionLabelClass} style={{ marginBottom: "6px" }}>
                Inicio
              </p>
            )}
            <Link
              to="/home"
              onClick={handleItemSelect}
              className={`group relative flex w-full items-center rounded-lg transition-colors ${
                isCollapsed ? "justify-center py-2 text-xs" : "px-4 py-2 gap-3 text-sm"
              } ${dashboardActive ? "text-slate-900" : "text-slate-600"} uppercase tracking-wide`}
              style={buildNavItemStyle(dashboardActive)}
              onMouseEnter={(e) => handleHoverEnter(e, dashboardActive)}
              onMouseLeave={(e) => handleHoverLeave(e, dashboardActive)}
            >
              {isCollapsed && dashboardActive && (
                <span
                  aria-hidden="true"
                  className="absolute left-1 top-1/2 h-8 w-1.5 -translate-y-1/2 rounded-r-full"
                  style={{ backgroundColor: sidebarActiveAccent }}
                />
              )}
              {!isCollapsed && (
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -left-[13px] top-1/2 h-3 w-3 -translate-y-1/2 transition-colors ${
                    dashboardActive ? "text-blue-200" : "text-slate-200 group-hover:text-slate-300"
                  }`}
                >
                  <svg viewBox="0 0 12 12" className="h-full w-full">
                    <path
                      d="M0 0 C0 6 6 6 12 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              )}
              <div
                className={`rounded-full flex items-center justify-center ${
                  isCollapsed ? "h-10 w-10" : "h-9 w-9"
                }`}
                style={{
                  backgroundColor: dashboardActive
                    ? `${colorTokens.actionPrimaryBase || "#6366f1"}1A`
                    : "rgba(15,23,42,0.08)",
                  color: colorTokens.contentDarkPrimary || "#0f172a",
                }}
              >
                <LayoutDashboard className="h-5 w-5" />
              </div>
              {!isCollapsed && <span className="truncate">Dashboard</span>}
            </Link>
          </div>

          <div
            className={`flex flex-col py-6 ${
              isCollapsed ? "items-center gap-3 px-2" : "px-2 gap-3"
            }`}
          >
            {!isCollapsed && (
              <p className={sectionLabelClass} style={{ marginBottom: "-4px" }}>
                Boards
              </p>
            )}
            {filteredMenuItems.map((section, i) => (
              <SidebarSection
                key={i}
                section={section}
                isCollapsed={isCollapsed}
                onCollapsedSectionSelect={handleCollapsedSectionSelect}
                onCollapsedHover={showCollapsedOverlay}
                onCollapsedLeave={() => hideCollapsedOverlay()}
                activePath={activePath}
                typography={navTypography}
                pendingSection={pendingSection}
                onPendingSectionHandled={() => setPendingSection(null)}
                onItemSelect={handleItemSelect}
              />
            ))}
          </div>

          {scrollThumb.scrollable && (
            <>
              <span aria-hidden="true" className="sidebar-scroll-track" />
              <span
                aria-hidden="true"
                className="sidebar-scroll-thumb"
                style={{
                  height: `${scrollThumb.height || 0}px`,
                  transform: `translateY(${scrollThumb.top}px)`,
                }}
              />
            </>
          )}
        </nav>

      </div>

      <CollapsedOverlay
        overlay={collapsedOverlay}
        activePath={activePath}
        onMouseEnter={() => {
          if (overlayHideTimeoutRef.current) {
            clearTimeout(overlayHideTimeoutRef.current);
            overlayHideTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => hideCollapsedOverlay()}
        onItemEnter={handleOverlayItemEnter}
        onItemLeave={handleOverlayItemLeave}
        onItemClick={handleOverlayItemClick}
      />

      <NestedOverlay
        overlay={nestedOverlay}
        activePath={activePath}
        onMouseEnter={() => {
          if (nestedHideTimeoutRef.current) {
            clearTimeout(nestedHideTimeoutRef.current);
            nestedHideTimeoutRef.current = null;
          }
          if (overlayHideTimeoutRef.current) {
            clearTimeout(overlayHideTimeoutRef.current);
            overlayHideTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => {
          hideNestedOverlay();
          hideCollapsedOverlay();
        }}
        onItemClick={handleOverlayItemClick}
      />
    </aside>
  );
}
